import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import AuthContainer from '../../components/AuthContainer'
import Header from '../../components/Header'
import { useSendOtpMutation, useVerifyOtpMutation } from '../../store/api/apiSlice'
import { useAppDispatch } from '../../hooks/useAppStore'
import { setCredentials } from '../../store/slices/authSlice'
import { parseErrorMessage } from '../../utils/parseErrorMessage'

export default function VerifyAccount() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const phoneFromState = useMemo(() => (location.state as any)?.phoneNumber || '', [location.state])
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation()
  const [sendOtp, { isLoading: isSending }] = useSendOtpMutation()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [resendTimer, setResendTimer] = useState(30)

  const phoneNumber = useMemo(
    () => phoneFromState || localStorage.getItem('pending_verification_phone') || '',
    [phoneFromState]
  )

  useEffect(() => {
    if (phoneNumber) {
      localStorage.setItem('pending_verification_phone', phoneNumber)
    }
  }, [phoneNumber])

  useEffect(() => {
    if (resendTimer <= 0) return
    const timerId = setInterval(() => setResendTimer((t) => t - 1), 1000)
    return () => clearInterval(timerId)
  }, [resendTimer])

  const handleChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code]
      newCode[index] = value
      setCode(newCode)

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`)
        nextInput?.focus()
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const digits = pasted.split('')
    setCode((prev) => prev.map((_d, idx) => digits[idx] || ''))
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    setSuccessMessage('')

    const otp = code.join('')
    if (otp.length !== 6) {
      setErrorMessage('Enter the 6-digit code sent to your phone.')
      return
    }

    if (!phoneNumber) {
      setErrorMessage('Missing phone number. Please restart verification from signup or login.')
      return
    }

    try {
      const result = await verifyOtp({ phoneNumber, otp }).unwrap()
      localStorage.removeItem('pending_verification_phone')

      // Auto-login: store tokens and redirect to dashboard
      if (result.access_token) {
        localStorage.setItem('access_token', result.access_token)
        if (result.refresh_token) {
          localStorage.setItem('refresh_token', result.refresh_token)
        }
        dispatch(setCredentials({
          user: result.user,
          token: result.access_token
        }))
        setSuccessMessage('Phone verified! Redirecting to dashboard...')
        setTimeout(() => navigate('/dashboard', { replace: true }), 500)
      } else {
        // Fallback if tokens not in response
        setSuccessMessage('Phone number verified! Redirecting to login...')
        setTimeout(() => navigate('/login', { replace: true }), 1000)
      }
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, 'Verification failed. Please try again.'))
    }
  }

  const handleResend = async () => {
    if (!phoneNumber || resendTimer > 0) return
    setErrorMessage('')
    setSuccessMessage('')

    try {
      await sendOtp({ phoneNumber }).unwrap()
      setSuccessMessage('A new OTP has been sent to your phone.')
      setResendTimer(30)
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, 'Could not resend OTP. Please try again.'))
    }
  }

  return (
    <div className='min-h-screen bg-[#FFFFFF] flex flex-col'>
      <Header />
      <AuthContainer>
      <div className='w-full'>
        {/* Header */}
        <div className='mb-8'>
          <h1 className='text-gray-900 text-[32px] md:text-[40px] font-bold mb-3'>
            Verify Your Account
          </h1>
          <p className='text-gray-600 text-[14px] md:text-[15px]'>
            Type the 6-digit code sent to your WhatsApp or email. This helps keep your account secure.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className='space-y-6'>
          {(errorMessage || successMessage) && (
            <div
              className={`border px-4 py-3 rounded-lg text-sm ${
                errorMessage ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
              }`}
            >
              {errorMessage || successMessage}
            </div>
          )}

          {/* OTP Input */}
          <div className='flex gap-3 justify-center md:justify-start'>
            {code.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type='text'
                inputMode='numeric'
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className='w-12 h-12 md:w-14 md:h-14 text-center text-xl font-semibold bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent'
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            type='submit'
            disabled={isVerifying}
            className='w-full bg-black text-white py-3 rounded-lg text-[15px] font-medium hover:bg-gray-900 transition-colors disabled:opacity-70 disabled:cursor-not-allowed'
          >
            {isVerifying ? 'Verifying...' : 'Verify & Continue'}
          </button>

          {/* Resend Code */}
          <div className='text-center'>
            <p className='text-gray-600 text-[14px]'>
              Didn't get the code?{' '}
              {resendTimer > 0 ? (
                <span className='text-orange-500 font-medium'>
                  Resend OTP in 00:{String(resendTimer).padStart(2, '0')}
                </span>
              ) : (
                <button
                  type='button'
                  onClick={handleResend}
                  disabled={isSending}
                  className='text-orange-500 hover:underline font-medium disabled:opacity-70 disabled:cursor-not-allowed'
                >
                  {isSending ? 'Sending...' : 'Resend OTP'}
                </button>
              )}
            </p>
            {!phoneNumber && (
              <p className='text-gray-500 text-[13px] mt-2'>Phone number is missing. Please restart from signup/login.</p>
            )}
          </div>
        </form>
      </div>
    </AuthContainer>
    </div>
  )
}
